import React, { useEffect, useState } from "react";
import { useLocalStorage } from "sprint-common-web";

import { useOrdersModuleApi } from "../../../common/useOrdersModuleApi";
import { defaultOnPremOrderListingConfig, OnPremOrderListingConfig } from "../config/OnPremOrderListingConfig";
import { OnPremOrderListingFilters } from "../hooks/useOnPremOrderListingFilters";
import { mapToOnPremOrderListingItem, OnPremOrderListingItem } from "../OnPremOrderListingItem";
import { OnPremOrderListingContext } from "./OnPremOrderListingContext";

export type OnPremOrderListingProviderProps = React.PropsWithChildren<unknown>;

const ONPREM_ORDER_LISTING_CONFIG_KEY = "onPremOrderListingConfig";
const PINNED_ONPREM_ORDERS_KEY = "pinnedOnPremOrders";

export const OnPremOrderListingProvider: React.FC<OnPremOrderListingProviderProps> = (
    props: OnPremOrderListingProviderProps
) => {
    const { getOnPremOrderList } = useOrdersModuleApi();

    const [items, setItems] = useState<OnPremOrderListingItem[]>([]);
    const [selectedItems, setSelectedItems] = useState<OnPremOrderListingItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);

    const [listingConfig, setListingConfig] = useLocalStorage<OnPremOrderListingConfig>(
        ONPREM_ORDER_LISTING_CONFIG_KEY,
        defaultOnPremOrderListingConfig()
    );

    const [pinnedItems, setPinnedItems] = useLocalStorage<string[]>(PINNED_ONPREM_ORDERS_KEY, []);

    useEffect(() => {
        setItems((prevItems) => {
            const pinned: OnPremOrderListingItem[] = [];
            const unpinned: OnPremOrderListingItem[] = [];

            prevItems.forEach((item) => {
                if (pinnedItems.includes(item.id)) {
                    pinned.push({ ...item, isPinned: true });
                } else {
                    unpinned.push({ ...item, isPinned: false });
                }
            });

            return [...pinned, ...unpinned];
        });
    }, [pinnedItems.length]);

    const reloadData = async (filters: OnPremOrderListingFilters): Promise<void> => {
        const response = await getOnPremOrderList({
            page: filters.page,
            pageSize: filters.pageSize,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
            statuses: filters.statuses,
            content: filters.content,
            distributorId: filters.distributorId,
            airlineIds: filters.airlineIds,
            systemIds: filters.systemIds,
            pONumber: filters.pONumber,
            filename: filters.filename,
            progresses: filters.progresses,
            sources: filters.sources,
            dueDateFrom: filters.dueDateFrom?.toISOString(),
            dueDateTo: filters.dueDateTo?.toISOString(),
            startDateFrom: filters.startDateFrom?.toISOString(),
            startDateTo: filters.startDateTo?.toISOString(),
            orderIds: pinnedItems
        });

        const listingItems = response.items.map((item) => mapToOnPremOrderListingItem(item, pinnedItems));

        setItems(listingItems);
        setTotalItems(response.totalCount);
    };

    const updateOnPremOrder = (
        onPremOrder: Partial<OnPremOrderListingItem> & Required<Pick<OnPremOrderListingItem, "id">>
    ) => {
        setItems((prevItems) =>
            prevItems.map((item) => (item.id === onPremOrder.id ? { ...item, ...onPremOrder } : item))
        );
    };

    const togglePinnedOnPremOrder = (orderId: string) => {
        setPinnedItems((prevState) =>
            prevState.includes(orderId) ? prevState.filter((item) => item !== orderId) : [...prevState, orderId]
        );
    };

    return (
        <OnPremOrderListingContext.Provider
            value={{
                items,
                selectedItems,
                totalItems,
                pinnedItems,
                listingConfig,
                saveListingConfig: setListingConfig,
                setSelectedItems,
                togglePinnedOnPremOrder,
                reloadData,
                updateOnPremOrder
            }}
        >
            {props.children}
        </OnPremOrderListingContext.Provider>
    );
};
